import React, { useState, useEffect, useCallback } from 'react';
import { orderService } from '../../api/orderService';
import {
  Package, User, Mail, Phone, MapPin, Calendar, CreditCard,
  FileText, Download, Truck, CheckCircle, XCircle, Clock,
  Tag, MessageSquare, AlertCircle, Hash, ShoppingCart,
} from 'lucide-react';
import './OrderDetails.css';

/* ─── tiny inline alert ─── */
const InlineAlert = ({ type, message }) => {
  if (!message) return null;
  const Icon = type === 'success' ? CheckCircle : AlertCircle;
  return (
    <div className={`inline-alert ${type}`}>
      <Icon size={16} />
      {message}
    </div>
  );
};

/* ─── helpers ─── */
const fmt = (n) => parseFloat(n || 0).toFixed(2);

const STATUS_CONFIG = {
  pending:   { label: 'En attente', badge: 'badge-warning' },
  accepted:      { label: 'Acceptée',       badge: 'badge-info'    },
  shipped:   { label: 'Expédié',    badge: 'badge-info'    },
  completed: { label: 'Terminé',    badge: 'badge-success' },
  cancelled: { label: 'Annulé',     badge: 'badge-danger'  },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, badge: 'badge-secondary' };
  return <span className={`badge ${cfg.badge}`}>{cfg.label}</span>;
};

const paymentLabel = (m) =>
  ({ cash: 'Espèces', card: 'Carte', transfer: 'Virement' }[m] || m || 'Non spécifié');

/* ─── main component ─── */
const OrderDetailsModal = ({ orderId, onClose, onStatusChange }) => {
  const [order,       setOrder]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [updating,    setUpdating]    = useState(false);
  const [canceling,   setCanceling]   = useState(false);
  const [newStatus,   setNewStatus]   = useState('');
  const [newPayment,  setNewPayment]  = useState('');
  const [notes,       setNotes]       = useState('');
  const [alert,       setAlert]       = useState({ type: '', message: '' });

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert({ type: '', message: '' }), 4000);
  };

  const fetchOrderDetails = useCallback(async () => {
    try {
      setLoading(true);
      const data = await orderService.getOrder(orderId);
      setOrder(data);
      setNewStatus(data.status);
      setNewPayment(data.payment_method || '');
    } catch {
      showAlert('error', 'Échec du chargement des détails de la commande');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { fetchOrderDetails(); }, [fetchOrderDetails]);

  /* close on Escape */
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleStatusUpdate = async () => {
    if (!newStatus) return;
    try {
      setUpdating(true);
      await orderService.updateOrderStatus(orderId, newStatus, notes || null);
      if (newPayment !== order.payment_method) {
        await orderService.updateOrder(orderId, { payment_method: newPayment });
      }
      setNotes('');
      await fetchOrderDetails();
      showAlert('success', 'Statut mis à jour avec succès');
      if (onStatusChange) onStatusChange();
    } catch {
      showAlert('error', 'Échec de la mise à jour du statut');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette commande ? Cette action est irréversible.')) return;
    try {
      setCanceling(true);
      await orderService.cancelOrder(orderId);
      await fetchOrderDetails();
      showAlert('success', 'Commande annulée avec succès');
      if (onStatusChange) onStatusChange();
    } catch (err) {
      showAlert('error', err.response?.data?.message || 'Échec de l\'annulation de la commande');
    } finally {
      setCanceling(false);
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      await orderService.downloadInvoice(orderId);
    } catch {
      showAlert('error', 'Échec du téléchargement de la facture');
    }
  };

  /* ── loading / empty states ── */
  if (loading) {
    return (
      <div className="order-details-modal">
        <div className="modal-overlay" onClick={onClose} />
        <div className="modal-content large">
          <div className="loading-spinner">Chargement…</div>
        </div>
      </div>
    );
  }

  if (!order) return null;

  /* ── derived values ── */
  const isFinal   = order.status === 'completed' || order.status === 'cancelled';
  const isChanged = newStatus !== order.status || newPayment !== (order.payment_method || '');

  const customerName  = order.client ? `${order.client.first_name} ${order.client.last_name}` : order.guest_name;
  const customerEmail = order.client ? order.client.email  : order.guest_email;
  const customerPhone = order.client ? order.client.phone  : order.guest_phone;

  /* shipping address — prefer dedicated fields, fall back to guest_* */
  const shippingAddress = order.shipping_address || order.guest_address;
  const shippingCity    = order.shipping_city    || order.guest_city;
  const shippingZip     = order.shipping_zip     || order.guest_zip;
  const shippingCountry = order.shipping_country || order.guest_country;
  const shippingState   = order.shipping_state   || order.guest_state;

  /* financial */
  const subtotal      = parseFloat(order.subtotal      || 0);
  const shippingFee   = parseFloat(order.shipping_fee  || 0);
  const discountAmt   = parseFloat(order.discount_amount || 0);
  const taxAmt        = parseFloat(order.tax_amount    || 0);
  const total         = parseFloat(order.total         || 0);
  const showBreakdown = subtotal > 0 || shippingFee > 0 || discountAmt > 0 || taxAmt > 0;

  return (
    <div className="order-details-modal">
      <div className="modal-overlay" onClick={onClose} />
      <div className="modal-content large">

        {/* ── header ── */}
        <div className="modal-header">
          <div>
            <h2>Commande #{order.id}</h2>
            <p className="modal-subtitle">
              <Hash size={13} />
              Facture&nbsp;: {order.invoice_number || 'N/A'}
              &nbsp;•&nbsp;
              <span className={`badge ${order.source === 'website' ? 'badge-info' : 'badge-secondary'}`}>
                {order.source === 'website' ? 'Site Web' : 'Magasin'}
              </span>
              &nbsp;•&nbsp;<StatusBadge status={order.status} />
            </p>
          </div>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        {/* ── body ── */}
        <div className="modal-body">

          {/* Status timeline */}
          <div className="status-timeline">
            {[
              { statuses: ['pending','accepted','shipped','completed'], icon: <Clock size={20} />,       label: 'En attente' },
              { statuses: ['accepted','shipped','completed'],           icon: <CreditCard size={20} />,  label: 'Acceptée'       },
              { statuses: ['shipped','completed'],                  icon: <Truck size={20} />,       label: 'Expédié'    },
              { statuses: ['completed'],                            icon: <CheckCircle size={20} />, label: 'Terminé'    },
            ].map((step, i) => (
              <div
                key={i}
                className={`timeline-step ${step.statuses.includes(order.status) ? 'active' : ''}`}
              >
                {step.icon}
                <span>{step.label}</span>
              </div>
            ))}
          </div>

          {/* ── 2-col info grid ── */}
          <div className="order-details-grid">

            {/* Customer */}
            <div className="details-card">
              <h3><User size={18} />Informations client</h3>
              <div className="info-list">
                <div className="info-item">
                  <User size={16} />
                  <div>
                    <span className="label">Nom</span>
                    <span className="value">
                      {customerName}
                      &nbsp;
                      {order.client
                        ? <span className="badge badge-success">Client enregistré</span>
                        : <span className="badge badge-secondary">Invité</span>}
                    </span>
                  </div>
                </div>
                <div className="info-item">
                  <Mail size={16} />
                  <div>
                    <span className="label">Email</span>
                    <span className="value">{customerEmail || 'N/A'}</span>
                  </div>
                </div>
                <div className="info-item">
                  <Phone size={16} />
                  <div>
                    <span className="label">Téléphone</span>
                    <span className="value">{customerPhone || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Order info */}
            <div className="details-card">
              <h3><Package size={18} />Informations de commande</h3>
              <div className="info-list">
                <div className="info-item">
                  <FileText size={16} />
                  <div>
                    <span className="label">Statut</span>
                    <span className="value"><StatusBadge status={order.status} /></span>
                  </div>
                </div>
                <div className="info-item">
                  <CreditCard size={16} />
                  <div>
                    <span className="label">Méthode de paiement</span>
                    <span className="value">{paymentLabel(order.payment_method)}</span>
                  </div>
                </div>
                <div className="info-item">
                  <Calendar size={16} />
                  <div>
                    <span className="label">Date de commande</span>
                    <span className="value">{new Date(order.created_at).toLocaleString('fr-FR')}</span>
                  </div>
                </div>
                {order.updated_at && order.updated_at !== order.created_at && (
                  <div className="info-item">
                    <Calendar size={16} />
                    <div>
                      <span className="label">Dernière mise à jour</span>
                      <span className="value">{new Date(order.updated_at).toLocaleString('fr-FR')}</span>
                    </div>
                  </div>
                )}
                {order.coupon_code && (
                  <div className="info-item">
                    <Tag size={16} />
                    <div>
                      <span className="label">Code promo</span>
                      <span className="value">{order.coupon_code}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Shipping address ── */}
          {(shippingAddress || shippingCity) && (
            <div className="details-card" style={{ marginBottom: '24px' }}>
              <h3><MapPin size={18} />Adresse de livraison</h3>
              <div className="info-list">
                {shippingAddress && (
                  <div className="info-item">
                    <MapPin size={16} />
                    <div>
                      <span className="label">Adresse</span>
                      <span className="value">{shippingAddress}</span>
                    </div>
                  </div>
                )}
                {(shippingCity || shippingZip) && (
                  <div className="info-item">
                    <MapPin size={16} />
                    <div>
                      <span className="label">Ville / Code postal</span>
                      <span className="value">
                        {[shippingCity, shippingZip].filter(Boolean).join(' — ')}
                      </span>
                    </div>
                  </div>
                )}
                {(shippingState || shippingCountry) && (
                  <div className="info-item">
                    <MapPin size={16} />
                    <div>
                      <span className="label">Région / Pays</span>
                      <span className="value">
                        {[shippingState, shippingCountry].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Order items ── */}
          <div className="details-card" style={{ marginBottom: '24px' }}>
            <h3><ShoppingCart size={18} />Articles commandés</h3>
            {order.order_items && order.order_items.length > 0 ? (
              <>
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th>Qté</th>
                      <th>Prix unitaire</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.order_items.map((item, idx) => {
                      const productName = item.product
                        ? item.product.name
                        : item.promo_pack
                          ? item.promo_pack.name
                          : item.product_name || 'N/A';

                      const imgSrc = item.product?.main_image
                        ? `${process.env.REACT_APP_API_URL}/storage/${item.product.main_image}`
                        : null;

                      const variantLabel = [
                        item.color  && `Couleur: ${item.color}`,
                        item.size   && `Taille: ${item.size}`,
                        item.variant_name,
                      ].filter(Boolean).join(' · ');

                      return (
                        <tr key={idx}>
                          <td>
                            <div className="product-info">
                              {imgSrc && (
                                <img
                                  src={imgSrc}
                                  alt={productName}
                                  className="product-thumb"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              )}
                              <div>
                                <div>{productName}</div>
                                {item.product?.sku && (
                                  <div style={{ fontSize: '11px', color: '#718096' }}>SKU: {item.product.sku}</div>
                                )}
                                {variantLabel && (
                                  <span className="variant-chip">{variantLabel}</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>{item.quantity}</td>
                          <td>{fmt(item.unit_price)} DH</td>
                          <td className="total-cell">{fmt(item.quantity * item.unit_price)} DH</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Financial breakdown */}
                <div className="financial-summary">
                  {showBreakdown ? (
                    <>
                      {subtotal > 0 && (
                        <div className="financial-row">
                          <span>Sous-total</span>
                          <span>{fmt(subtotal)} DH</span>
                        </div>
                      )}
                      {shippingFee > 0 && (
                        <div className="financial-row">
                          <span>Frais de livraison</span>
                          <span>{fmt(shippingFee)} DH</span>
                        </div>
                      )}
                      {discountAmt > 0 && (
                        <div className="financial-row discount">
                          <span>Remise</span>
                          <span>- {fmt(discountAmt)} DH</span>
                        </div>
                      )}
                      {taxAmt > 0 && (
                        <div className="financial-row">
                          <span>TVA</span>
                          <span>{fmt(taxAmt)} DH</span>
                        </div>
                      )}
                      <div className="financial-row total">
                        <span>Total</span>
                        <span>{fmt(total)} DH</span>
                      </div>
                    </>
                  ) : (
                    <div className="financial-row total">
                      <span>Total</span>
                      <span>{fmt(total)} DH</span>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <p style={{ textAlign: 'center', color: '#718096', padding: '20px' }}>
                Aucun article trouvé
              </p>
            )}
          </div>

          {/* ── Customer notes ── */}
          {order.notes && (
            <div className="details-card" style={{ marginBottom: '24px' }}>
              <h3><MessageSquare size={18} />Notes du client</h3>
              <div className="notes-block">{order.notes}</div>
            </div>
          )}

          {/* ── Status management ── */}
          <div className="details-card">
            <h3><FileText size={18} />Gestion du statut</h3>

            <InlineAlert type={alert.type} message={alert.message} />

            {isFinal ? (
              <p style={{ color: '#718096', fontSize: '14px', fontStyle: 'italic' }}>
                Cette commande est <strong>{STATUS_CONFIG[order.status]?.label}</strong> et ne peut plus être modifiée.
              </p>
            ) : (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Nouveau statut</label>
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="form-control"
                    >
                      <option value="pending">En attente</option>
                      <option value="accepted">Acceptée</option>
                      <option value="shipped">Expédié</option>
                      <option value="completed">Terminé</option>
                      <option value="cancelled">Annulé</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Méthode de paiement</label>
                    <select
                      value={newPayment}
                      onChange={(e) => setNewPayment(e.target.value)}
                      className="form-control"
                    >
                      <option value="">Non spécifié</option>
                      <option value="cash">Espèces</option>
                      <option value="card">Carte</option>
                      <option value="transfer">Virement</option>
                    </select>
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label>Notes internes (optionnel)</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="form-control"
                    rows="3"
                    placeholder="Ajouter des notes sur cette mise à jour…"
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-primary"
                    onClick={handleStatusUpdate}
                    disabled={updating || (!isChanged && !notes)}
                  >
                    {updating ? 'Mise à jour…' : 'Enregistrer les modifications'}
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={handleCancelOrder}
                    disabled={canceling || updating}
                  >
                    <XCircle size={18} />
                    {canceling ? 'Annulation…' : 'Annuler la commande'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── footer ── */}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Fermer</button>
          <button className="btn btn-success" onClick={handleDownloadInvoice}>
            <Download size={18} />
            Télécharger la facture
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;
